import { Icon, Intent, MenuItem, Position } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { ItemSelector, Text, Tooltip } from 'vet-bones/components';

import {
  ProjectGeoDataByIri,
  ProjectGeoDataProperty,
} from 'src/constants/projectGeoData';
import {
  PROJECT_DIAGRAM_ENTITY_DRAWER_ATTRIBUTE_TYPE,
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
} from 'src/constants/testIds';
import { AttributeTableItem } from 'src/containers/ProjectDiagram/components/attributesTable/types';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import * as copy from 'src/templates/copy';

type ProjectDiagramClassDrawerGeospatialInputProps = {
  attributeItems: AttributeTableItem[];
  geoProperty: ProjectGeoDataProperty | undefined;
  geoPropertyKey: keyof ProjectGeoDataByIri;
  inputLabel: string;
  onChangeGeoProperty: (property: ProjectGeoDataProperty | undefined) => void;
  showInvalidWarning: boolean;
};

const STRING_ITEM_VALUE = '__value__';

const wktPointRegex = new RegExp(
  /^POINT\(\s*(-?\d+(\.\d+)?)\s+(-?\d+(\.\d+)?)\s*\)/,
  'i'
);

export const createNewGeospatialStaticValueRenderer =
  (geoPropertyKey: keyof ProjectGeoDataByIri) =>
  (_items: AttributeTableItem[], testId?: string) =>
  // eslint-disable-next-line react/display-name
  (
    query: string,
    active: boolean,
    handleClick: React.MouseEventHandler<HTMLElement>
  ) => {
    // eslint-disable-next-line react/destructuring-assignment
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return undefined;
    }

    let errorMessage = '';
    const isMatched = trimmedQuery.match(wktPointRegex);
    if (geoPropertyKey === 'loc' && !isMatched) {
      errorMessage =
        copy.components.projectDiagram.classDrawer.geospatialTable.validation.location(
          trimmedQuery
        );
    }

    if (errorMessage) {
      return (
        <MenuItem
          className="sd-menu-item-no-results"
          icon={IconNames.BLANK}
          intent={Intent.DANGER}
          text={<Text ellipsize>{errorMessage}</Text>}
        />
      );
    }

    return (
      <MenuItem
        active={active}
        data-testid={testId}
        icon={IconNames.ADD}
        onClick={handleClick}
        shouldDismissPopover={false}
        text={copy.components.projectDiagram.classDrawer.geospatialTable.createStaticText(
          trimmedQuery
        )}
      />
    );
  };

export const ProjectDiagramClassDrawerGeospatialInput: React.VFC<
  ProjectDiagramClassDrawerGeospatialInputProps
> = ({
  attributeItems,
  geoProperty,
  geoPropertyKey,
  inputLabel,
  onChangeGeoProperty,
  showInvalidWarning,
}) => {
  const diagram = useDiagram();

  const selectedAttributeItem = React.useMemo<AttributeTableItem | null>(() => {
    if (!geoProperty) {
      return null;
    }

    if (geoProperty?.isAttribute) {
      return (
        attributeItems.find((item) => item.value === geoProperty.value) || null
      );
    }

    return {
      depth: 1,
      text: geoProperty?.value || '',
      value: STRING_ITEM_VALUE,
      ranges: [],
    };
  }, [attributeItems, geoProperty]);

  const geoPropertyLabel = React.useMemo(() => {
    if (!geoProperty || !diagram) {
      return '';
    }

    if (!geoProperty.isAttribute) {
      return `"${geoProperty.value}"`;
    }

    return getLabelForIri(
      diagram.model.modelData.labelsByIri,
      geoProperty.value
    );
  }, [diagram, geoProperty]);

  const handleSelectAttribute = (selected: AttributeTableItem | null) => {
    if (!selected) {
      onChangeGeoProperty(undefined);
      return;
    }

    onChangeGeoProperty({
      value: selected.value,
      isAttribute: !selected.isNewlyCreated,
    });
  };

  return (
    <tr>
      <td>
        <div>
          <Text>{inputLabel}</Text>
          {showInvalidWarning ? (
            <Tooltip
              content={
                copy.components.projectDiagram.classDrawer.geospatialTable
                  .invalidWarningTooltip
              }
              position={Position.BOTTOM}
            >
              <Icon icon={IconNames.WARNING_SIGN} />
            </Tooltip>
          ) : null}
        </div>
      </td>
      <td>
        <ItemSelector.Single
          buttonText={geoPropertyLabel}
          clearable
          creatable
          createNewItemRenderer={createNewGeospatialStaticValueRenderer(
            geoPropertyKey
          )}
          createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
            ''
          )}
          filterOrCreateText={copy.components.defaults.filterOrCreate}
          filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
          items={attributeItems}
          itemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
          onSelect={handleSelectAttribute}
          resetOnClose
          selected={selectedAttributeItem}
          testId={getProjectDiagramEntityDrawerSelectorTestId(
            PROJECT_DIAGRAM_ENTITY_DRAWER_ATTRIBUTE_TYPE
          )}
        />
      </td>
    </tr>
  );
};
